import { Outlet, createFileRoute, redirect, useMatchRoute } from '@tanstack/react-router'
import { Permission } from '../../features/users/types'
import { isAuthorized } from '../../features/users/services'
import { useAppStore, useLocalStore } from '~/stores'
import { BannerPortal, Boundary } from '../../components'
import { WorkspaceUsageCallout } from '../../features/billing/components/WorkspaceUsageCallout'
import { UnpaidInvoiceCallout } from '../../features/billing/components/UnpaidInvoiceCallout'
import { useCurrentRouteId, useIsAuthorized } from '~/hooks'
import { config } from '~/shared'
import { setTag } from '@sentry/react'
import { getQueryOptions } from '~/services'
import { ColoredAlert } from '@bpinternal/ui-kit-next'
import { getWorkspacePreference } from '~/services/preferences/workspacePreferences'
import { initSupportWebchat } from '~/utils/support-webchat'
import { FileRouteTypes } from '~/routeTree.gen'

const BANNER_DISABLED_PAGES: FileRouteTypes['to'][] = [
  '/workspaces/$workspaceId/bots/$botId/new-bot',
  '/workspaces/$workspaceId/bots/$botId/new-chat',
]

export const Route = createFileRoute('/workspaces/$workspaceId')({
  beforeLoad: async ({ context, params }) => {
    initSupportWebchat(params.workspaceId)
    const members = await context.queryClient.ensureQueryData(
      getQueryOptions('workspaces_/$workspaceId_/members', { workspaceId: params.workspaceId })
    )
    const workspaceMember = members?.find((member) => member.userId === context.user.id)
    const isUserAuthorized = (permission: Permission): boolean | undefined => {
      if (!workspaceMember) {
        return undefined
      }

      return isAuthorized({ permission, role: workspaceMember?.role })
    }
    return { isUserAuthorized }
  },
  loader: async ({ context, params, location }) => {
    const { setActiveWorkspace } = useAppStore.getState()
    const { setLastActiveWorkspaceId } = useLocalStore.getState()
    const workspace = await context.queryClient.fetchQuery(
      getQueryOptions('workspaces_/$workspaceId_', { workspaceId: params.workspaceId })
    )
    setActiveWorkspace(workspace)
    setLastActiveWorkspaceId(workspace.id)

    const onboardingCompleted = await getWorkspacePreference(
      {
        path: 'onBoardingCompleted',
      },
      params.workspaceId
    )

    if (workspace.botCount === 0 && !location.pathname.endsWith('/create-bot') && !onboardingCompleted) {
      // + settings
      throw redirect({ to: '/workspaces/$workspaceId/create-bot', params: { workspaceId: params.workspaceId } })
    }
  },
  component: Component,
})

function Component() {
  const { workspaceId } = Route.useParams()
  const { user } = Route.useRouteContext()
  const hasAccess = useIsAuthorized({ workspaceId, userId: user.id })
  setTag('workspaceId', workspaceId)

  const matchRoute = useMatchRoute()
  const isBannerEnabled = !BANNER_DISABLED_PAGES.some((route) => matchRoute({ to: route }))
  const routeId = useCurrentRouteId()

  function isWebchatLocation(): boolean {
    if (!routeId) return false

    return (
      routeId.includes('/webchat/v2/identity') ||
      routeId.includes('/webchat/v2/appearance') ||
      routeId.includes('/webchat/v2/deploy-settings') ||
      routeId.includes('/webchat/v2/features') ||
      routeId.includes('/webchat/v2/') ||
      routeId.includes('/webchat')
    )
  }

  return (
    <>
      {isBannerEnabled && (
        <BannerPortal>
          <Boundary loader={null} onError={() => null}>
            {hasAccess('workspace.manageBilling') && <UnpaidInvoiceCallout workspaceId={workspaceId} />}
            <WorkspaceUsageCallout workspaceId={workspaceId} className={isWebchatLocation() ? 'xl:w-[47.5%]' : ''} />
          </Boundary>
          {!user.emailVerified && (
            <ColoredAlert className={`my-2 bg-blue-50 ${isWebchatLocation() ? 'xl:w-[47.5%]' : ''}`}>
              Your email address is not verified. Email verification will be enforced soon.{' '}
              <a
                className="text-neutral-950 underline"
                target="_blank"
                rel="noreferrer"
                href={`${config.ssoBaseUrl}/verification`}
              >
                Verify now
              </a>
            </ColoredAlert>
          )}
        </BannerPortal>
      )}
      <Outlet />
    </>
  )
}
